import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {
  Check as CheckIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { companiesSelector } from 'app/selectors/companiesSelector';
import { addError, addInfo } from 'app/actions/snackbar/snackbar';
import { AccessLevel } from '@/api-gen/api.schemas';
import type { AccessLevel as AccessLevelType, SimpleCompanyDto } from '@/api-gen/api.schemas';
import { rolesShareRole, useRolesGetRoleCompanies } from '@/api-gen/api';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/i18n/types/i18nNamespace';
import type { I18nCommonNs, I18nScenarioEditorNs } from '@/i18n/dictionaries/interfaces';

interface RoleShareWithCompanyDialogProps {
  open: boolean;
  onClose: () => void;
  roleId?: number;
  roleName?: string | null;
  onShared?: () => void;
}

interface CompanyWithAccess {
  company: SimpleCompanyDto;
  access: AccessLevelType;
}

const RoleShareWithCompanyDialog: React.FC<RoleShareWithCompanyDialogProps> = ({
  open,
  onClose,
  roleId,
  roleName,
  onShared
}) => {
  const dispatch = useDispatch();
  const allCompanies = useSelector(companiesSelector);
  const [translate] = useTranslation([I18nNamespace.ScenarioEditor, I18nNamespace.Common]);
  const [selectedCompaniesWithAccess, setSelectedCompaniesWithAccess] = useState<CompanyWithAccess[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: existingSharesResponse } = useRolesGetRoleCompanies(roleId!, {
    query: {
      enabled: !!roleId && open
    }
  });

  const existingShares = Array.isArray(existingSharesResponse?.data)
    ? existingSharesResponse.data
    : Array.isArray(existingSharesResponse)
    ? existingSharesResponse
    : [];

  const availableCompanies = useMemo(() => {
    const sharedCompanyIds = new Set(existingShares.map((share) => share.companyId));
    return allCompanies.filter((company) => !sharedCompanyIds.has(company.id));
  }, [allCompanies, existingShares]);

  const handleCompaniesChange = (_: unknown, newCompanies: SimpleCompanyDto[]) => {
    const existingMap = new Map(selectedCompaniesWithAccess.map((item) => [item.company.id, item.access]));

    const newSelection = newCompanies.map((company) => ({
      company,
      access: existingMap.get(company.id) ?? AccessLevel.CanRead
    }));

    setSelectedCompaniesWithAccess(newSelection);
  };

  const handleAccessChange = (companyId: number | undefined, access: AccessLevelType) => {
    if (!companyId) return;

    setSelectedCompaniesWithAccess((prev) =>
      prev.map((item) => (item.company.id === companyId ? { ...item, access } : item))
    );
  };

  const handleSubmit = async () => {
    if (!roleId || selectedCompaniesWithAccess.length === 0) return;

    try {
      setIsSubmitting(true);

      await Promise.all(
        selectedCompaniesWithAccess.map(({ company, access }) =>
          rolesShareRole(roleId, {
            companyId: company.id!,
            accessLevel: access
          })
        )
      );

      const sharedCompanyName = selectedCompaniesWithAccess.length === 1 ? selectedCompaniesWithAccess[0].company.name : null;

      dispatch(
        addInfo({
          infoText: sharedCompanyName
            ? translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.roleShared), {
                roleName: roleName || '',
                companyName: sharedCompanyName
              })
            : 'Role shared with selected companies.'
        })
      );

      handleClose();
      onShared?.();
    } catch (error: any) {
      dispatch(
        addError({
          errorText:
            error?.message ||
            translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.roleShareError))
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedCompaniesWithAccess([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.shareRole))}
      </DialogTitle>
      <DialogContent>
        <DialogContentText gutterBottom>Select one or more companies and assign access level.</DialogContentText>

        <FormControl fullWidth sx={{ mt: 2 }}>
          <Autocomplete
            multiple
            id="role-shared-companies-select"
            options={availableCompanies}
            getOptionLabel={(option) => option.name || ''}
            value={selectedCompaniesWithAccess.map((item) => item.company)}
            onChange={handleCompaniesChange}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={<CheckIcon style={{ visibility: 'hidden' }} />}
                  checkedIcon={<CheckIcon />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                <Typography variant="body2">{option.name}</Typography>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Companies" placeholder="Select companies" />
            )}
          />
          <FormHelperText>
            {selectedCompaniesWithAccess.length === 0
              ? 'Choose companies to share with.'
              : `${selectedCompaniesWithAccess.length} selected`}
          </FormHelperText>
        </FormControl>

        {selectedCompaniesWithAccess.length > 0 && (
          <Box mt={3}>
            <Typography variant="subtitle2" gutterBottom>
              {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.accessLevel))}
            </Typography>
            <Stack spacing={2} mt={1}>
              {selectedCompaniesWithAccess.map(({ company, access }) => (
                <Box
                  key={company.id}
                  sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body1" fontWeight={500}>
                        {company.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <RadioGroup row value={access} onChange={(e) => handleAccessChange(company.id, e.target.value as AccessLevelType)}>
                        <FormControlLabel
                          value={AccessLevel.CanRead}
                          control={<Radio size="small" />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <ViewIcon fontSize="small" sx={{ mr: 0.5 }} />
                              <Typography variant="body2">
                                {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.canRead))}
                              </Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          value={AccessLevel.CanEdit}
                          control={<Radio size="small" />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <EditIcon fontSize="small" sx={{ mr: 0.5 }} />
                              <Typography variant="body2">
                                {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.canEdit))}
                              </Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          value={AccessLevel.CanAdministrate}
                          control={<Radio size="small" />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <AdminIcon fontSize="small" sx={{ mr: 0.5 }} />
                              <Typography variant="body2">
                                {translate(
                                  nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.canAdministrate)
                                )}
                              </Typography>
                            </Box>
                          }
                        />
                      </RadioGroup>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          {translate('common:' + nameof.full<I18nCommonNs>((n) => n.buttonLabels.cancel))}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || selectedCompaniesWithAccess.length === 0}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
        >
          {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.shareRole))}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleShareWithCompanyDialog;
